import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Palette } from 'Palette';
import { HEADING_FONT_SIZE_STANDARD, HEADING_SPACING } from 'DesignTokens';

/**
 * Shared typography tokens. These are the single source of truth for the fonts,
 * base text size/weight and heading scale used by the rendered UI — importable
 * so other surfaces (e.g. the v2 editor's in-place fields) can mirror the final
 * output without copying values. Changing a value here changes both.
 */
export const FONT_MANROPE = 'Manrope, sans-serif';
export const FONT_INTER = 'Inter, sans-serif';

export const BASE_TEXT_STYLE = {
  fontFamily: FONT_INTER,
  fontSize: '1rem',
  fontWeight: 400,
} as const;

export const HEADING_VARIANT_STYLE = {
  heading2: { fontSize: '1.5rem', fontWeight: 700 },
  subheading: { fontSize: '1rem', fontWeight: 600 },
  heroValue: { fontSize: '2.75rem', fontWeight: 700 },
  standard: { fontSize: HEADING_FONT_SIZE_STANDARD, fontWeight: 700 },
} as const;

export const Root = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
}));

type MuiTypographyProps = React.ComponentProps<typeof Typography>;
type StyledTypographyProps = Omit<MuiTypographyProps, 'variant'> & {
  tone?: keyof typeof Palette;
  fontWeight?: number;
  component?: string;
  className?: string;
  fontSize?: string | number;
  color?: string;
  variant?: MuiTypographyProps['variant'] | 'warning' | 'error';
};

export const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => !['tone', 'fontWeight', 'fontSize'].includes(prop as string),
})<StyledTypographyProps>(
  ({
    tone = 'default',
    fontWeight,
    component,
    fontSize = BASE_TEXT_STYLE.fontSize,
    color,
    variant,
  }: StyledTypographyProps) => ({
    color:
      color || variant === 'warning'
        ? Palette.warning.text
        : variant === 'error'
          ? Palette.error.text
          : Palette[tone].text,
    fontFamily: BASE_TEXT_STYLE.fontFamily,
    fontWeight: fontWeight || BASE_TEXT_STYLE.fontWeight,
    fontSize: fontSize,
    component,
    className: `react-StyledTypography ${component}`.trim(),
    backgroundColor:
      variant === 'warning'
        ? Palette.warning.background
        : variant === 'error'
          ? Palette.error.background
          : undefined,
    border:
      variant === 'warning'
        ? `2px solid ${Palette.warning.border}`
        : variant === 'error'
          ? `2px solid ${Palette.error.border}`
          : undefined,
  }),
);

const StyledHeadingBase = styled(StyledTypography, {
  shouldForwardProp: (prop) =>
    prop !== 'variant' &&
    prop !== 'placeholder' &&
    prop !== 'headerVariant' &&
    prop !== 'reduceBottomPadding' &&
    prop !== 'fullTopSpacing' &&
    prop !== 'noTopPadding',
})<{
  placeholder?: boolean;
  headerVariant?: 'heading2' | 'subheading' | 'standard' | 'heroValue';
  reduceBottomPadding?: boolean;
  noTopPadding?: boolean;
  fullTopSpacing?: boolean;
}>(({
  headerVariant = 'standard',
  reduceBottomPadding = false,
  noTopPadding = false,
  fullTopSpacing = false,
}) => {
  const padding = reduceBottomPadding
    ? HEADING_SPACING.paddingBottomReduced
    : HEADING_SPACING.paddingBottom;
  const { fontSize, fontWeight } =
    HEADING_VARIANT_STYLE[headerVariant] ?? HEADING_VARIANT_STYLE.standard;

  return {
    fontSize,
    paddingTop: noTopPadding
      ? 0
      : fullTopSpacing
        ? HEADING_SPACING.paddingTopFull
        : HEADING_SPACING.paddingTop,
    fontFamily: FONT_MANROPE,
    fontWeight,
    paddingBottom: padding,
  };
});

type StyledHeadingProps = React.ComponentProps<typeof StyledHeadingBase>;

export const StyledHeading = React.forwardRef<HTMLElement, StyledHeadingProps>((props, ref) => {
  const { className, headerVariant = 'standard', ...rest } = props;
  const mergedClassName = `react-StyledHeading heading-${headerVariant} ${className || ''}`.trim();
  return React.createElement(StyledHeadingBase, {
    ...rest,
    ref,
    headerVariant: headerVariant,
    className: mergedClassName,
  });
});

export const SectionTitle = styled(Typography)<{
  tone?: keyof typeof Palette;
  clearMargin?: boolean;
  className?: string;
}>(({ theme, tone = 'default', clearMargin = false, className }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: theme.spacing(clearMargin ? 0 : 2),
  color: Palette[tone]?.text || Palette.default.text,
  className: `react-SectionTitle ${className || ''}`.trim(),
}));
